/**
 * Drives one Source-with-Agent run over SSE and the per-candidate unlock.
 *
 * Streaming follows the same pattern as useJobNotifications: fetchEventSource
 * with a bearer token, parsing each `data:` frame as a JSON event and folding it
 * into AgentRunState. Unlock is a separate, blocking POST (the backend holds the
 * connection while the paid lookup completes); its progress is tracked per
 * candidate so each row animates independently.
 */
import { useCallback, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchEventSource } from '@microsoft/fetch-event-source';

import {
  EMPTY_RUN,
  type AgentCandidate,
  type AgentCriteria,
  type AgentRunState,
  type CandidateUnlock,
} from './types';

const API_URL = import.meta.env.VITE_API_SERVER_URL as string;

export interface UnlockResult {
  profileId: string;
  talentId: string;
  fullName?: string | null;
  cached: boolean;
  source: string;
}

export function useSourcingAgent(jobId: string) {
  const { getAccessTokenSilently } = useAuth0();
  const [state, setState] = useState<AgentRunState>(EMPTY_RUN);
  const abortRef = useRef<AbortController | null>(null);

  const token = useCallback(
    () =>
      getAccessTokenSilently({
        authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
      }),
    [getAccessTokenSilently],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState(EMPTY_RUN);
  }, []);

  const patchCandidate = useCallback(
    (dedupKey: string, unlock: CandidateUnlock) =>
      setState((prev) => ({
        ...prev,
        candidates: prev.candidates.map((c) =>
          c.dedupKey === dedupKey ? { ...c, unlock } : c,
        ),
      })),
    [],
  );

  const run = useCallback(
    async (query: string, criteria: AgentCriteria) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setState({ ...EMPTY_RUN, phase: 'running' });

      const apply = (event: string, data: Record<string, unknown>) => {
        setState((prev) => {
          switch (event) {
            case 'need':
              return { ...prev, need: { title: String(data.title ?? ''), chips: (data.chips as never) ?? [] } };
            case 'analysis':
              return { ...prev, analysis: String(data.text ?? '') };
            case 'criteria':
              return {
                ...prev,
                criteria: { text: String(data.text ?? ''), items: (data.items as never) ?? [] },
              };
            case 'search_reasoning':
              return { ...prev, searchReasoning: String(data.text ?? '') };
            case 'search_step': {
              const index = Number(data.index ?? 0);
              const label = String(data.label ?? '');
              const status = (data.status === 'done' ? 'done' : 'running') as 'done' | 'running';
              const existing = prev.steps.find((s) => s.index === index);
              const steps = existing
                ? prev.steps.map((s) => (s.index === index ? { ...s, label, status } : s))
                : [...prev.steps, { index, label, status }];
              return { ...prev, steps };
            }
            case 'search_result':
              return { ...prev, resultCount: Number(data.count ?? 0) };
            case 'search_debug':
              return {
                ...prev,
                debug: {
                  attempts: (data.attempts as never) ?? [],
                  count: Number(data.count ?? 0),
                },
              };
            case 'final': {
              const incoming = ((data.candidates as AgentCandidate[]) ?? []).map((c) => ({
                ...c,
                unlock: { status: 'locked' as const },
              }));
              return { ...prev, candidates: incoming };
            }
            case 'done':
              return { ...prev, phase: 'done' };
            case 'error':
              return { ...prev, phase: 'error', error: String(data.message ?? '') };
            default:
              return prev;
          }
        });
      };

      try {
        const bearer = await token();
        await fetchEventSource(`${API_URL}/api/sourcing/agent/run`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${bearer}`,
          },
          body: JSON.stringify({ job_id: jobId, query, criteria }),
          signal: controller.signal,
          openWhenHidden: true,
          async onopen(res) {
            // A non-stream response (e.g. 429 quota) won't emit SSE frames —
            // pull its `detail` code so the UI can show a precise message.
            const ct = res.headers.get('content-type') ?? '';
            if (res.ok && ct.includes('text/event-stream')) return;
            let detail = '';
            try {
              detail = String((await res.json())?.detail ?? '');
            } catch {
              /* no JSON body */
            }
            throw new Error(detail || `run_failed_${res.status}`);
          },
          onmessage(ev) {
            if (!ev.data) return;
            try {
              const payload = JSON.parse(ev.data) as { event: string } & Record<string, unknown>;
              apply(payload.event, payload);
            } catch {
              /* ignore malformed frame */
            }
          },
          onerror(err) {
            // Stop retrying — surface as an error state and bail out.
            setState((prev) => ({ ...prev, phase: 'error', error: errMessage(err) }));
            throw err;
          },
        });
      } catch (err) {
        setState((prev) =>
          prev.phase === 'done'
            ? prev
            : { ...prev, phase: prev.phase === 'error' ? 'error' : 'error', error: prev.error || errMessage(err) },
        );
      }
    },
    [jobId, token],
  );

  const unlock = useCallback(
    async (candidate: AgentCandidate): Promise<UnlockResult> => {
      patchCandidate(candidate.dedupKey, { status: 'unlocking' });
      try {
        const bearer = await token();
        const res = await fetch(`${API_URL}/api/sourcing/agent/unlock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
          body: JSON.stringify({
            source: candidate.source,
            dedup_key: candidate.dedupKey,
            linkedin_url: candidate.linkedinUrl ?? undefined,
            source_id: candidate.sourceId ?? undefined,
            job_id: jobId,
          }),
        });
        if (!res.ok) {
          let detail = `unlock_failed_${res.status}`;
          try {
            detail = String((await res.json())?.detail ?? detail);
          } catch {
            /* no JSON body */
          }
          throw new Error(detail);
        }
        const data = (await res.json()) as UnlockResult;
        patchCandidate(candidate.dedupKey, {
          status: 'unlocked',
          profileId: data.profileId,
          cached: data.cached,
        });
        return data;
      } catch (err) {
        patchCandidate(candidate.dedupKey, { status: 'error', error: errMessage(err) });
        throw err;
      }
    },
    [jobId, patchCandidate, token],
  );

  return { state, run, unlock, reset };
}

function errMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
